import { Box, HStack, VStack } from "@chakra-ui/react"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import Card from "@pathwright/ui/src/components/card/Card"
import { Card as CardT } from "@pathwright/ui/src/components/card/types"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Success from "@pathwright/ui/src/components/success/Success"
import PathwrightUI from "@pathwright/ui/src/components/ui/PathwrightUI"
import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import {
  CohortSwitcherCohortsDocument,
  CreateGroupMutationOptions,
  RegistrationRoleEnum,
  ResourceType,
  useCreateGroupMutation,
  useCreateResourceMutation,
  useResourceQuery
} from "../../api/generated"
import { useCohortSyncPlanQuery } from "../../path/sync/SyncPlanContext"
import { ResourceTypeSchema } from "../../resource/constants"
import { getResourceTypeLabel } from "../../resource/utils"
import { getGroupUrl } from "../../utils/urls"
import CohortNameInput from "./CohortNameInput"
import ResourcePicker from "./ResourcePicker"
import ResourceTypeSwitcher, {
  ResourceTypeTooltip
} from "./ResourceTypeSwitcher"
import UpgradeCohortsGate from "./UpgradeCohortsGate"

export const CreateCohortCard = ({
  card,
  resourceId,
  registerCreatorAsRole,
  onCohortCreated
}: {
  card: CardT
  resourceId: number
  registerCreatorAsRole: RegistrationRoleEnum
  onCohortCreated?: CreateGroupMutationOptions["onCompleted"]
}) => {
  const [createdCohortId, setCreatedCohortId] = useState<number | null>(null)

  const { query: cohortSyncPlanQuery } = useCohortSyncPlanQuery({
    cohortId: createdCohortId
  })

  const handleCohortCreated: CreateGroupMutationOptions["onCompleted"] = async (
    data
  ) => {
    if (data?.createGroup) {
      setCreatedCohortId(data?.createGroup?.id)

      if (cohortSyncPlanQuery) {
        await cohortSyncPlanQuery.refetch({
          cohortId: data?.createGroup?.id
        })
        setCreatedCohortId(null)
        if (onCohortCreated) {
          onCohortCreated(data)
        }
      }
    }
  }

  return (
    <StyledCard card={card} noaction>
      <Box p={4} pt={0}>
        <CreateCohort
          resourceId={resourceId}
          registerCreatorAsRole={registerCreatorAsRole}
          onClose={card.onClose}
          onCohortCreated={handleCohortCreated}
        />
      </Box>
    </StyledCard>
  )
}

CreateCohortCard.displayName = "CreateCohortCard"

const CreateCohort = ({
  resourceId: preSelectedResourceId,
  registerCreatorAsRole,
  onClose,
  onCohortCreated
}: {
  resourceId?: number | null
  registerCreatorAsRole: RegistrationRoleEnum
  onClose: () => void
  onCohortCreated?: CreateGroupMutationOptions["onCompleted"]
}) => {
  const cohortNameInputRef = useRef<HTMLInputElement>(null)
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(
    preSelectedResourceId || null
  )
  const [resourceType, setResourceType] = useState<ResourceType>(
    ResourceType.Course
  )
  const [resourceName, setResourceName] = useState("")
  const [cohortName, setCohortName] = useState("")
  const [resourceCoverImage, setResourceCoverImage] = useState<string | null>(
    null
  )

  const { t, tc } = useTranslate()

  // for an unknown reason, have to handle handle the change in the preSelectedResourceId
  // subsequent mounts of the CreateCohort seem to not set the provided resourceId prop in state
  useEffect(() => {
    preSelectedResourceId && setSelectedResourceId(preSelectedResourceId)
  }, [preSelectedResourceId])

  // optionally query a preselected resource
  const preSelectedResourceQuery = useResourceQuery({
    variables: { id: preSelectedResourceId! },
    skip: !preSelectedResourceId
  })

  const resourceQuery = useResourceQuery({
    variables: { id: selectedResourceId! },
    skip: !selectedResourceId
  })

  const preSelectedResource = preSelectedResourceQuery.data?.resource
  const selectedResource = resourceQuery.data?.resource

  // When pre/selected resource changes, update the resource type.
  useEffect(() => {
    const resource = preSelectedResource || selectedResource
    if (resource) {
      setResourceType(ResourceTypeSchema.parse(resource.resource_type))
    }
  }, [preSelectedResource, selectedResource])

  function handleAutoFocusingCohortNameInput() {
    // Try focusing immediately, for best UX.
    cohortNameInputRef.current?.focus()
    // Add a slight delay to ensure focus action occurs after any other.
    setTimeout(() => {
      cohortNameInputRef.current?.focus()
    }, 1)
  }

  // Auto focus cohort name input when user selects an existing resource.
  useEffect(() => {
    if (selectedResource) handleAutoFocusingCohortNameInput()
  }, [selectedResource])

  const onCompleted: CreateGroupMutationOptions["onCompleted"] = (...args) => {
    // Reset to initial state (necessry if form is not unmounted before user completes again).
    if (!preSelectedResource) setResourceName("")
    setCohortName("")
    return onCohortCreated?.(...args)
  }

  const [createResourceCohort, createResourceCohortMutation] =
    useCreateResourceMutation({
      variables: {
        name: resourceName,
        cover_image: resourceCoverImage!,
        resource_type: resourceType,
        group_name: cohortName,
        group_role: registerCreatorAsRole
      },
      onCompleted,
      refetchQueries: ["LibraryResources", "Cohorts", "UserResources"]
    })

  const [createCohort, createCohortMutation] = useCreateGroupMutation({
    variables: {
      base_resource_id: selectedResourceId!,
      visibility: 5, // Private, required variable
      name: cohortName,
      role: registerCreatorAsRole
    },
    onCompleted,
    refetchQueries: [
      "Cohorts",
      {
        query: CohortSwitcherCohortsDocument,
        variables: { resourceId: selectedResourceId }
      }
    ]
  })

  // // TODO: make sure checking path.last_synced_dtime is accurate
  // // if the resource's source cohort has not yet been synced, prompt user to sync to the source
  // if (
  //   preSelectedResource &&
  //   !preSelectedResource.source_cohort.path.last_synced_dtime &&
  //   // cohort exists and has been synced
  //   // NOTE: ignoring the fact that multiple cohorts could exist in which 1 or more have been synced yet the source path has not
  //   // we'll just not handle that case as this is more of a helper prompt
  //   preSelectedResource.cohorts.total === 1 &&
  //   preSelectedResource.cohorts.edges[0].node.path.last_synced_dtime
  // ) {
  //   // NOTE: not handling this prompt for now until we can test a check that works!
  //   // return (
  //   //   <SyncSourcePromptCard
  //   //     card={card}
  //   //     resource={preSelectedResource}
  //   //     cohortId={preSelectedResource.cohorts.edges[0].node.id}
  //   //   />
  //   // )
  // }

  const formIsValid = !!((selectedResource || resourceName) && cohortName)

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (formIsValid) {
      return selectedResource ? createCohort() : createResourceCohort()
    }
  }

  // TODO: check for cohorts included in plan
  // How do we handle curriculum subscribers?
  // How do we handle non-activated schools?
  // How do we handle licensed resources?

  if (createResourceCohortMutation.data?.createResource) {
    const {
      createResource: resource,
      createResource: { cohort }
    } = createResourceCohortMutation.data

    return (
      <PathwrightUI.Provider
        theme={{
          backgroundImage: resource.blurred_background,
          backgroundOverlay: resource.background_overlay
        }}
      >
        <Success
          icon="check"
          heading={t("New Path & Cohort Created.")}
          body={t("The next step is to design the learning path.")}
          onBackgroundClick={onClose}
          primaryAction={{
            label: (
              <>
                <Pathicon icon="group" />
                <span> </span>
                {t("Design Path")}
              </>
            ),
            // Linking to the source cohort since editing the source path
            // makes the most sense after creating a new resource + cohort.
            // to: getGroupUrl(resource.slug, cohort.source_cohort_id, "path"),
            // Current opinion is that most users will not be concerned with
            // the source path, so direct them to the created cohort path.
            to: getGroupUrl(resource.slug, cohort?.id!, "path"),
            onClick: onClose
          }}
          secondaryAction={{
            label: "Create another",
            onClick: () => createResourceCohortMutation.reset()
          }}
        />
      </PathwrightUI.Provider>
    )
  }

  if (createCohortMutation.data?.createGroup) {
    const {
      createGroup: cohort,
      createGroup: { resource }
    } = createCohortMutation.data

    return (
      <PathwrightUI.Provider
        theme={{
          backgroundImage: resource?.blurred_background,
          backgroundOverlay: resource?.background_overlay
        }}
      >
        <Success
          icon="check"
          heading={t("Cohort Created")}
          body={t(`Your new Cohort in {{ resourceName }} is ready.`, {
            resourceName: resource?.name
          })}
          onBackgroundClick={onClose}
          primaryAction={{
            label: (
              <>
                <Pathicon icon="group" />
                <span> </span>
                {t("View Cohort")}
              </>
            ),
            to: `${getGroupUrl(
              resource?.slug!,
              cohort.id,
              "path"
            )}?path_mode=15`,
            onClick: onClose
          }}
          secondaryAction={{
            label: "Create another",
            onClick: () => createCohortMutation.reset()
          }}
        />
      </PathwrightUI.Provider>
    )
  }

  return (
    <CreateCohortContainer className="CreateCohort">
      <UpgradeCohortsGate resourceId={selectedResourceId!} overlayLoading>
        {preSelectedResourceQuery.loading ? (
          <LoadingCircle />
        ) : (
          <form onSubmit={onSubmit}>
            <VStack spacing={3}>
              {preSelectedResource ? (
                <HStack w="full" justify="flex-start">
                  <span>{getResourceTypeLabel(resourceType, tc)}</span>
                  <ResourceTypeTooltip resourceType={resourceType} />
                </HStack>
              ) : (
                <ResourceTypeSwitcher
                  resourceType={resourceType}
                  onSwitchResourceType={setResourceType}
                  prompt={t("Create or select a")}
                />
              )}
              <ResourcePicker
                resourceType={resourceType}
                onChangeResourceName={setResourceName}
                preSelectedResource={preSelectedResource}
                selectedResource={selectedResource}
                onSelectResource={(resource) =>
                  setSelectedResourceId(resource ? resource.id : null)
                }
                onSelectResourceCoverImage={setResourceCoverImage}
                onOpenStore={onClose}
                onFocusCohortNameInput={handleAutoFocusingCohortNameInput}
              />
              <CohortNameInput
                ref={cohortNameInputRef}
                resourceType={resourceType}
                onChangeCohortName={setCohortName}
                selectedResource={selectedResource}
                resourceCoverImage={resourceCoverImage}
              />
              <SubmitButton
                styleType="primary"
                size="large"
                onClick={onSubmit}
                disabled={!formIsValid}
              >
                {selectedResource
                  ? t("Create Cohort")
                  : t("Create {{ resourceType }} + Cohort", {
                      resourceType: getResourceTypeLabel(resourceType, tc)
                    })}
              </SubmitButton>
            </VStack>
          </form>
        )}
      </UpgradeCohortsGate>
    </CreateCohortContainer>
  )
}

CreateCohort.displayName = "CreateCohort"

const StyledCard = styled(Card)`
  .CreateCohort {
    padding: 0;
  }
`

const CreateCohortContainer = styled.div`
  background-color: #fff;
  border-radius: 20px;
  padding: 20px;
  max-width: 680px;
  margin: 0px auto;

  .Tooltip {
    min-width: 200px;
    white-space: normal;
    text-align: left;
  }
  .Tooltip__trigger {
    display: flex !important;
  }
`

export default CreateCohort
